import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';
// mui
import { Box, Typography } from '@mui/material';
// components
import { Iconify } from '../index';
import WidgetButtons from './WidgetButtons';
// redux
import { useSelector } from '../../redux/store';
// hooks
import { useToggle } from '../../hooks';
// utils
import { UpdateTableData } from '../../utils/allTableTab';
import { getSelectedWidgetStyle } from '../../utils/reportWidgetUtils';
// style
import {
    widgetCardStyle,
    widgetCardWrapperStyle,
    widgetCounterTextStyle,
    widgetCounterWrapperStyle,
    widgetTextStyle,
    widgetIconWrapper,
    getWidgetUrgencyBarColor,
    getWidgetBadgeStyle,
    widgetBadgeDotStyle,
    getUrgencyFromCount,
    getWidgetTextColor,
    getWidgetIconBgColor,
} from '../../styles/widgetCounterStyle';

/** Generate a short status badge text matching the Dashboard style */
const getBadgeText = (key, count) => {
    if (count === 0) {
        if (key === 'FAILED_TO_ATTEND') return 'All rebooked';
        return 'All on track';
    }
    if (count >= 10) {
        if (key === '1') return `${count} need contact`;
        if (key === 'FAILED_TO_ATTEND') return `${count} to rebook`;
        return `${count} need action`;
    }
    if (key === '1') return `${count} to contact`;
    if (key === 'FAILED_TO_ATTEND') return 'Rebook these';
    return `${count} to follow up`;
};

const DashBoardReportsWidget = ({
    dateRangeProps, onChangeTab, activeWidgetProps: { currentWidget, setCurrentWidget }, widgetToggle }) => {
    const { widgetCounter } = useSelector((state) => state.widgetCounter);

    const navigate = useNavigate();

    const { toggle: internalToggle, onOpen, onClose } = useToggle(true);
    const toggle = widgetToggle !== undefined ? widgetToggle : internalToggle;

    const tabCounter = (data) => widgetCounter?.filter((value) => (value.key === data ? value.count : null));

    const handleOpen = () => onOpen();

    const handleClose = () => onClose();

    const handleWidgetClick = (ele) => {
        const dateRange = dateRangeProps ? [{ startDate: dateRangeProps.startDate, endDate: dateRangeProps.endDate }] : [];
        if (ele.key === '1' && onChangeTab) {
            setCurrentWidget(ele.key);
            onChangeTab('', 'newLeads');
        } else if (ele.key === '1') {
            UpdateTableData('DashNewLeadTable', { date: dateRange });
            UpdateTableData('DashNewLeadTableForWidget', { date: dateRange });
            navigate('/dashboard/app', { state: { isNewLeadTab: true } });
            setCurrentWidget(ele.key);
        }
        if (ele.key === '2' && onChangeTab) {
            onChangeTab('', 'To-do', true, 'NEW');
            setCurrentWidget(ele.key);
        }
        if (ele.key === 'BOOKED') {
            navigate('/dashboard/tables/consultant-booked-table');
            UpdateTableData('ConsultantBooked', { date: dateRange });
            setCurrentWidget(ele.key);
        }
        if (ele.key === '4') {
            navigate('/dashboard/tables/consultant-attended-table');
            UpdateTableData('ConsultantAttend', { date: dateRange });
            setCurrentWidget(ele.key);
        }
        if (ele.key === 'FAILED_TO_ATTEND') {
            navigate('/dashboard/tables/failed-to-attend-table');
            setCurrentWidget(ele.key);
        }
    };

    const widgetArray = [
        { key: '2', icon: 'icomoon-free:phone', text: 'New patient follow ups', bgColor: '#FFE7D9', textColor: '#B72136' },
        { key: '4', icon: 'icomoon-free:phone', text: 'Consultation follow ups', bgColor: '#FFE7D9', textColor: '#B72136' },
        { key: '7', icon: 'logos:whatsapp-icon', text: 'Pending Whatsapps', bgColor: '#dcfce7', textColor: '#007867' },
        { key: '1', icon: 'streamline:user-add-plus-solid', text: 'New patient leads to contact', bgColor: '#D0F2FFCC', textColor: '#0052CC' },
        { key: 'FAILED_TO_ATTEND', icon: 'ri:calendar-close-fill', text: 'Consultation FTA\'s', bgColor: '#FFE7D9', textColor: '#B72136' },
        { key: 'BOOKED', icon: 'ic:baseline-schedule', text: 'Consultations booked', bgColor: '#EDE7F6', textColor: '#5119B7' },
    ];

    return (
        <>
            {dateRangeProps && <WidgetButtons toggle={toggle} dateRangeProps={dateRangeProps} handleOpen={handleOpen} handleClose={handleClose} type='dashboard_widget' />}
            {toggle && (
                <Box sx={widgetCardWrapperStyle}>
                    {
                        widgetArray.filter((ele) => {
                            if (ele.key === 'FAILED_TO_ATTEND') {
                                const count = tabCounter(ele.key)?.[0]?.count || 0;
                                return count > 0;
                            }
                            return true;
                        }).map((ele) => {
                            const count = tabCounter(ele.key)?.[0]?.count || 0;
                            const urgency = getUrgencyFromCount(count);
                            const badgeText = getBadgeText(ele.key, count);
                            const barColor = getWidgetUrgencyBarColor(urgency);
                            const textColor = getWidgetTextColor(urgency);
                            const iconBg = getWidgetIconBgColor(urgency);

                            return (<Box
                                key={ele.key}
                                sx={{
                                    ...widgetCardStyle,
                                    ...getSelectedWidgetStyle(ele.key, currentWidget, barColor),
                                }}
                                onClick={() => handleWidgetClick(ele)}
                            >
                                {/* Left accent bar */}
                                {barColor !== 'transparent' && (
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        bottom: 0,
                                        width: '4px',
                                        background: barColor,
                                        borderRadius: '10px 0 0 10px',
                                    }} />
                                )}
                                <Box sx={widgetCounterWrapperStyle}>
                                    <Typography sx={{ ...widgetCounterTextStyle, color: textColor }}>
                                        {count}
                                    </Typography>
                                    {ele.icon &&
                                        <Box sx={{ ...widgetIconWrapper, bgcolor: iconBg }}>
                                            <Iconify icon={ele.icon} width={16} height={16} sx={{ color: textColor }} />
                                        </Box>}
                                </Box>
                                <Typography sx={{ ...widgetTextStyle, color: textColor }}>{ele.text}</Typography>
                                {/* Urgency badge */}
                                <Box component="span" sx={getWidgetBadgeStyle(urgency)}>
                                    <Box component="span" sx={widgetBadgeDotStyle(barColor)} />
                                    {badgeText}
                                </Box>
                            </Box>)
                        })}
                </Box>
            )}
        </>
    );
};

export default DashBoardReportsWidget;

DashBoardReportsWidget.propTypes = {
    dateRangeProps: PropTypes.any,
    onChangeTab: PropTypes.func,
    activeWidgetProps: PropTypes.any,
    widgetToggle: PropTypes.bool,
};
